import { createContext, useContext, useEffect, useMemo, useReducer } from 'react';

const CartCtx = createContext(null);
export const useCart = () => useContext(CartCtx);

// ===== utils =====
const clamp = (n, a, b) => Math.max(a, Math.min(b, n));
const nnum  = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const sid   = (v) => String(v ?? '');

// единый формат элемента корзины
function normItem(r) {
  if (!r) return null;
  const id = sid(r.id ?? r._id ?? r.productId);
  if (!id) return null;
  const qty = clamp(nnum(r.qty) || 1, 1, 999);
  const price = nnum(
    (typeof r.price === 'number' ? r.price : undefined) ??
    r.price?.effective ??
    r.priceRetail ?? 0
  );
  const img =
    r.img || r.image || r.images?.[0]?.url || r.product?.images?.[0]?.url || '';
  return {
    id,
    slug: sid(r.slug ?? r.product?.slug),
    title: r.title ?? r.product?.title ?? '',
    img,
    price,
    qty
  };
}

function dedupe(list) {
  const map = new Map();
  for (const x of list) {
    const n = normItem(x);
    if (!n) continue;
    const prev = map.get(n.id);
    if (!prev) map.set(n.id, n);
    else map.set(n.id, { ...prev, qty: clamp(prev.qty + n.qty, 1, 999) });
  }
  return Array.from(map.values());
}

function readLS() {
  try {
    const raw = localStorage.getItem('cart');
    const arr = Array.isArray(JSON.parse(raw)) ? JSON.parse(raw) : [];
    return dedupe(arr);
  } catch { return []; }
}

function writeLS(items) {
  try { localStorage.setItem('cart', JSON.stringify(items)); } catch {}
}

// ===== reducer =====
const ACT = {
  INIT: 'INIT',
  ADD: 'ADD',
  INC: 'INC',
  DEC: 'DEC',
  SET: 'SET',
  REMOVE: 'REMOVE',
  CLEAR: 'CLEAR'
};

function reducer(state, action) {
  switch (action.type) {
    case ACT.INIT: {
      return dedupe(action.items || []);
    }
    case ACT.ADD: {
      const { product, qty = 1 } = action;
      const id = sid(product._id ?? product.id);
      if (!id) return state;
      const base = [...state];
      const i = base.findIndex(x => x.id === id);
      if (i === -1) {
        base.push(normItem({
          id,
          slug: product.slug,
          title: product.title,
          img: product.images?.[0]?.url,
          price: product.price?.effective ?? product.priceRetail ?? 0,
          qty: clamp(nnum(qty) || 1, 1, 999)
        }));
      } else {
        base[i] = { ...base[i], qty: clamp(base[i].qty + (nnum(qty) || 1), 1, 999) };
      }
      return base;
    }
    case ACT.INC: {
      const base = state.map(x =>
        x.id === action.id ? { ...x, qty: clamp(x.qty + 1, 1, 999) } : x
      );
      return base;
    }
    case ACT.DEC: {
      const base = state.map(x =>
        x.id === action.id ? { ...x, qty: clamp(x.qty - 1, 1, 999) } : x
      );
      return base;
    }
    case ACT.SET: {
      const q = clamp(nnum(action.qty) || 1, 1, 999);
      return state.map(x => (x.id === action.id ? { ...x, qty: q } : x));
    }
    case ACT.REMOVE:
      return state.filter(x => x.id !== action.id);
    case ACT.CLEAR:
      return [];
    default:
      return state;
  }
}

export function CartProvider({ children }) {
  const [items, dispatch] = useReducer(reducer, [], readLS); // lazy init из LS

  // сохраняем только в один ключ
  useEffect(() => { writeLS(items); }, [items]);

  // на всякий — вычищаем старые ключи (чтобы ничего «не возвращалось» после перезагрузки)
  useEffect(() => {
    try { localStorage.removeItem('ls_cart_v1'); localStorage.removeItem('basket'); } catch {}
  }, []);

  const value = useMemo(() => {
    const totalQty = items.reduce((s, i) => s + i.qty, 0);
    const totalSum = items.reduce((s, i) => s + i.qty * i.price, 0);
    return {
      items,
      totalQty,
      totalSum,
      add: (product, qty = 1) => dispatch({ type: ACT.ADD, product, qty }),
      inc: (id) => dispatch({ type: ACT.INC, id: sid(id) }),
      dec: (id) => dispatch({ type: ACT.DEC, id: sid(id) }),
      setQty: (id, qty) => dispatch({ type: ACT.SET, id: sid(id), qty }),
      remove: (id) => dispatch({ type: ACT.REMOVE, id: sid(id) }),
      clear: () => dispatch({ type: ACT.CLEAR })
    };
  }, [items]);

  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
}
